import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft, Heart, Star, BedDouble, Bath, Maximize2, MapPin,
  CheckCircle, ChevronLeft, ChevronRight, Send,
  Calculator, School, ShieldCheck, Coffee, Share2, Copy, X,
  Phone, Mail, Globe, Building2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURE_LABELS = {
  pool:       { label: 'Pool',        icon: '🏊' },
  solar:      { label: 'Solar',       icon: '⚡' },
  adu:        { label: 'ADU / Casita',icon: '🏠' },
  no_hoa:     { label: 'No HOA',      icon: '✓' },
  garage:     { label: 'Garage',      icon: '🚗' },
  ev_charger: { label: 'EV Charger',  icon: '🔌' },
  has_pool:    { label: 'Pool',        icon: '🏊' },
  has_solar:   { label: 'Solar',       icon: '⚡' },
  has_adu:     { label: 'ADU / Casita',icon: '🏠' },
  has_hoa:     { label: 'HOA',         icon: '' },
  has_garage:  { label: 'Garage',      icon: '🚗' },
  has_ev_charger: { label: 'EV Charger', icon: '🔌' },
};

// Deterministic "neighborhood" values based on listing id hash
function neighborhoodStats(id = '') {
  const hash = id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return {
    walkScore:   55 + (hash % 40),
    schoolRating: 6 + (hash % 4) + '.' + (hash % 10),
    crimeLevel:  ['Low', 'Low', 'Moderate', 'Low'][hash % 4],
    cafes:       4 + (hash % 12),
  };
}

// Mortgage calculation
function calcMonthly(price, downPct, rate, years) {
  const P = price * (1 - downPct / 100);
  const r = rate / 100 / 12;
  const n = years * 12;
  if (r === 0) return P / n;
  return (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

// ─── Score Bar ─────────────────────────────────────────────────────────────────
function ScoreBar({ label, earned, max }) {
  const pct = max > 0 ? Math.round((earned / max) * 100) : 0;
  const color = pct >= 80 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-600 font-medium">{label}</span>
        <span className="font-bold" style={{ color }}>{earned}/{max}</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="h-full rounded-full" style={{ background: color }} />
      </div>
    </div>
  );
}

// ─── Photo Gallery ─────────────────────────────────────────────────────────────
function PhotoGallery({ photos }) {
  const [current, setCurrent] = useState(0);
  const imgs = photos?.length ? photos : ['https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=1200&q=80'];

  return (
    <div className="relative h-72 sm:h-96 bg-slate-200 overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.img key={current} src={imgs[current]}
          initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.35 }}
          className="w-full h-full object-cover"
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=1200&q=80'; }}
          alt="listing" />
      </AnimatePresence>
      {imgs.length > 1 && (
        <>
          <button onClick={() => setCurrent(p => (p - 1 + imgs.length) % imgs.length)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-all">
            <ChevronLeft size={18} className="text-white" />
          </button>
          <button onClick={() => setCurrent(p => (p + 1) % imgs.length)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-all">
            <ChevronRight size={18} className="text-white" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {imgs.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)}
                className="w-2 h-2 rounded-full transition-all"
                style={{ background: i === current ? 'white' : 'rgba(255,255,255,0.45)' }} />
            ))}
          </div>
        </>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
    </div>
  );
}

// ─── Contact Form ──────────────────────────────────────────────────────────────
function ContactForm({ listingId, authFetch }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    if (!message.trim()) { setError('Please write a message'); return; }
    setSending(true);
    setError('');
    try {
      const r = await authFetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing_id: listingId, message }),
      });
      if (r.ok) {
        setSent(true);
      } else {
        const d = await r.json();
        setError(d.detail || 'Failed to send message');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setSending(false);
  };

  if (sent) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8" data-testid="contact-sent">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
          <CheckCircle size={22} className="text-emerald-500" />
        </div>
        <p className="font-bold text-slate-800">Message sent!</p>
        <p className="text-sm text-slate-500 mt-1">The agent will be in touch with you soon.</p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-3" data-testid="contact-form">
      <textarea
        placeholder="Hi, I'm interested in this property. Can we schedule a showing?"
        value={message} onChange={e => setMessage(e.target.value)} rows={4}
        data-testid="contact-message"
        className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-100 transition-all resize-none" />
      {error && <p className="text-xs text-red-500 font-medium" data-testid="contact-error">{error}</p>}
      <button onClick={handleSend} disabled={sending}
        data-testid="contact-send-btn"
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60"
        style={{ background: '#0A192F' }}>
        {sending ? (
          <div className="w-4 h-4 rounded-full animate-spin" style={{ border: '2px solid white', borderTopColor: 'transparent' }} />
        ) : (
          <><Send size={14} />Contact Agent</>
        )}
      </button>
    </div>
  );
}

// ─── Share Button ──────────────────────────────────────────────────────────────
function ShareButton({ address, price }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = window.location.href;
  const text = encodeURIComponent(`Check out this home on Goldielocks: ${address} — $${price?.toLocaleString()}`);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); } catch { }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`, '_blank');

  const nativeShare = () => {
    if (navigator.share) navigator.share({ title: `Goldielocks — ${address}`, url });
    else setOpen(p => !p);
  };

  return (
    <div className="relative">
      <button onClick={nativeShare} data-testid="share-btn"
        className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center hover:bg-slate-50 hover:border-slate-300 transition-all">
        <Share2 size={16} className="text-slate-500" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 6, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            className="absolute right-0 top-12 bg-white rounded-2xl border border-slate-200 shadow-xl p-3 z-50 min-w-[180px]">
            <button onClick={() => setOpen(false)}
              className="absolute top-2 right-2 p-1 rounded-lg hover:bg-slate-100 transition-colors">
              <X size={12} className="text-slate-400" />
            </button>
            <p className="text-xs font-bold text-slate-600 mb-3 pr-6">Share this home</p>
            <button onClick={copyLink} data-testid="copy-link-btn"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
              {copied ? <CheckCircle size={14} className="text-emerald-500" /> : <Copy size={14} className="text-slate-400" />}
              {copied ? 'Copied!' : 'Copy link'}
            </button>
            <button onClick={shareTwitter} data-testid="share-twitter-btn"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
              <span className="text-slate-500 font-black text-sm">𝕏</span>
              Share on X
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
function MortgageEstimator({ price }) {
  const [open, setOpen] = useState(false);
  const [down, setDown] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(30);

  const monthly = calcMonthly(price, down, rate, years);
  const loanAmt = price * (1 - down / 100);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
      <button onClick={() => setOpen(p => !p)}
        data-testid="mortgage-toggle"
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center">
            <Calculator size={15} className="text-indigo-500" />
          </div>
          <span className="font-bold text-sm text-slate-800">Mortgage Estimator</span>
        </div>
        <span className="text-xs font-semibold text-indigo-500">
          {open ? 'Hide' : `~$${Math.round(monthly).toLocaleString()}/mo`}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
            className="overflow-hidden">
            <div className="px-5 pb-5 space-y-4 border-t border-slate-100">
              <div className="grid grid-cols-3 gap-3 pt-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1.5">Down Payment</label>
                  <div className="relative">
                    <input type="number" min={0} max={100} value={down} onChange={e => setDown(+e.target.value)}
                      data-testid="mortgage-down"
                      className="w-full pr-6 pl-3 py-2 rounded-xl border border-slate-200 text-sm text-right outline-none focus:border-indigo-300 transition-all" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1.5">Interest Rate</label>
                  <div className="relative">
                    <input type="number" min={0} max={30} step={0.1} value={rate} onChange={e => setRate(+e.target.value)}
                      data-testid="mortgage-rate"
                      className="w-full pr-6 pl-3 py-2 rounded-xl border border-slate-200 text-sm text-right outline-none focus:border-indigo-300 transition-all" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1.5">Term</label>
                  <select value={years} onChange={e => setYears(+e.target.value)}
                    data-testid="mortgage-term"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-indigo-300 transition-all">
                    <option value={15}>15 yr</option>
                    <option value={20}>20 yr</option>
                    <option value={30}>30 yr</option>
                  </select>
                </div>
              </div>
              <div className="bg-indigo-50 rounded-xl p-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs font-semibold text-slate-500">Est. monthly payment</span>
                  <span className="text-2xl font-black text-indigo-600" data-testid="monthly-payment">
                    ${Math.round(monthly).toLocaleString()}
                  </span>
                </div>
                <div className="mt-2 flex gap-4 text-xs text-slate-500">
                  <span>Loan: <b>${Math.round(loanAmt).toLocaleString()}</b></span>
                  <span>Down: <b>${Math.round(price * down / 100).toLocaleString()}</b></span>
                </div>
                <p className="text-xs text-slate-400 mt-2">*Estimate only. Does not include taxes, insurance, or HOA fees.</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Neighborhood Stats ────────────────────────────────────────────────────────
function NeighborhoodStats({ id }) {
  const s = neighborhoodStats(id);
  const crimeColor = s.crimeLevel === 'Low' ? '#10B981' : '#F59E0B';

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <h3 className="font-bold text-sm text-slate-800 mb-4">Neighborhood (Estimated)</h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-slate-50 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Coffee size={13} className="text-amber-500" />
            <span className="text-xs font-semibold text-slate-500">Walk Score</span>
          </div>
          <div className="text-xl font-black text-slate-800">{s.walkScore}<span className="text-xs font-normal text-slate-400">/100</span></div>
        </div>
        <div className="bg-slate-50 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <School size={13} className="text-indigo-500" />
            <span className="text-xs font-semibold text-slate-500">Schools</span>
          </div>
          <div className="text-xl font-black text-slate-800">{s.schoolRating}<span className="text-xs font-normal text-slate-400">/10</span></div>
        </div>
        <div className="bg-slate-50 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <ShieldCheck size={13} style={{ color: crimeColor }} />
            <span className="text-xs font-semibold text-slate-500">Crime</span>
          </div>
          <div className="text-lg font-black" style={{ color: crimeColor }}>{s.crimeLevel}</div>
        </div>
        <div className="bg-slate-50 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <MapPin size={13} className="text-emerald-500" />
            <span className="text-xs font-semibold text-slate-500">Nearby</span>
          </div>
          <div className="text-lg font-black text-slate-800">{s.cafes} <span className="text-xs font-normal text-slate-400">places</span></div>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-3">* Neighborhood data is estimated and for reference only.</p>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function ListingDetail() {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { authFetch, user } = useAuth();

  const [listing, setListing] = useState(state?.listing || null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [savingHeart, setSavingHeart] = useState(false);

  // Always fetch from API to get full data including agent_branding
  useEffect(() => {
    setLoading(true);
    authFetch(`${API}/api/listings/${id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setListing(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]); // eslint-disable-line

  // Check if already saved
  useEffect(() => {
    authFetch(`${API}/api/buyer/saved`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setSaved(data.some(l => l.id === id)))
      .catch(() => {});
  }, [id]); // eslint-disable-line

  const toggleSave = async () => {
    setSavingHeart(true);
    const method = saved ? 'DELETE' : 'POST';
    await authFetch(`${API}/api/buyer/save/${id}`, { method });
    setSaved(p => !p);
    setSavingHeart(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 rounded-full animate-spin"
          style={{ border: '3px solid #0A192F', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <p className="text-slate-500 text-lg font-semibold">Listing not found.</p>
        <button onClick={() => navigate('/discover')}
          className="mt-4 px-6 py-2.5 rounded-xl text-sm font-bold text-white"
          style={{ background: '#0A192F' }}>
          Back to Discover
        </button>
      </div>
    );
  }

  const score = listing.match_score;
  const scoreColor = score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : '#94A3B8';
  const features = [
    listing.has_pool && 'Pool',
    listing.has_solar && 'Solar',
    listing.has_adu && 'ADU / Casita',
    listing.has_garage && 'Garage',
    listing.has_ev_charger && 'EV Charger',
    !listing.has_hoa && 'No HOA',
  ].filter(Boolean);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Back */}
      <button onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4 transition-colors">
        <ArrowLeft size={16} />Back to listings
      </button>

      {/* Hero Photo */}
      <div className="rounded-2xl overflow-hidden mb-6 shadow-md">
        <PhotoGallery photos={listing.photos} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Main content */}
        <div className="lg:col-span-2 space-y-5">
          {/* Header */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <MapPin size={11} />{listing.city}, {listing.state} {listing.zip}
                </p>
                <h1 className="text-lg sm:text-xl font-black text-slate-900 leading-tight">{listing.address}</h1>
                <p className="text-2xl font-black mt-2" style={{ color: '#0A192F' }}>
                  ${listing.price?.toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {score != null && (
                  <div className="flex flex-col items-center px-3 py-2 rounded-xl border-2"
                    style={{ borderColor: scoreColor + '40', background: scoreColor + '10' }}>
                    <div className="flex items-center gap-1">
                      <Star size={12} fill={scoreColor} style={{ color: scoreColor }} />
                      <span className="text-lg font-black" style={{ color: scoreColor }}>{score}%</span>
                    </div>
                    <span className="text-xs text-slate-400 font-medium">match</span>
                  </div>
                )}
                <ShareButton address={listing.address} price={listing.price} />
                <button onClick={toggleSave} disabled={savingHeart}
                  data-testid="detail-save-btn"
                  className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center hover:bg-red-50 hover:border-red-200 transition-all">
                  <Heart size={18} className={saved ? 'text-red-500 fill-red-500' : 'text-slate-400'} />
                </button>
              </div>
            </div>

            {/* Specs */}
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center">
                  <BedDouble size={15} className="text-slate-500" />
                </div>
                <div>
                  <div className="font-bold text-slate-800">{listing.beds}</div>
                  <div className="text-xs text-slate-400">Bedrooms</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center">
                  <Bath size={15} className="text-slate-500" />
                </div>
                <div>
                  <div className="font-bold text-slate-800">{listing.baths}</div>
                  <div className="text-xs text-slate-400">Bathrooms</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center">
                  <Maximize2 size={15} className="text-slate-500" />
                </div>
                <div>
                  <div className="font-bold text-slate-800">{listing.sqft?.toLocaleString()}</div>
                  <div className="text-xs text-slate-400">Sq Ft</div>
                </div>
              </div>
              {listing.has_hoa && listing.hoa_amount > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center">
                    <span className="text-slate-500 text-xs font-bold">HOA</span>
                  </div>
                  <div>
                    <div className="font-bold text-slate-800">${listing.hoa_amount}/mo</div>
                    <div className="text-xs text-slate-400">HOA Fee</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Features */}
          {features.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="font-bold text-sm text-slate-800 mb-3">Features</h3>
              <div className="flex flex-wrap gap-2">
                {features.map(f => (
                  <span key={f}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                    <CheckCircle size={11} className="text-emerald-500" />
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {listing.description && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="font-bold text-sm text-slate-800 mb-2">About This Home</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{listing.description}</p>
            </div>
          )}

          {/* Mortgage Estimator */}
          <MortgageEstimator price={listing.price} />

          {/* Neighborhood */}
          <NeighborhoodStats id={id} />
        </div>

        {/* Right: Sidebar */}
        <div className="space-y-5">
          {/* Match Score Breakdown */}
          {listing.match_breakdown && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-sm text-slate-800">Match Breakdown</h3>
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black text-white"
                  style={{ background: scoreColor }}>
                  <Star size={10} fill="white" />{score}%
                </div>
              </div>
              <div className="space-y-3">
                {Object.values(listing.match_breakdown).map(b => (
                  <ScoreBar key={b.label} label={b.label} earned={b.earned} max={b.max} />
                ))}
              </div>
              {listing.met_features?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-2">Must-haves met</p>
                  <div className="flex flex-wrap gap-1.5">
                    {listing.met_features.map(f => (
                      <span key={f} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                        {FEATURE_LABELS[f]?.label || f}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Form */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <h3 className="font-bold text-sm text-slate-800 mb-1">Contact Agent</h3>
            <p className="text-xs text-slate-400 mb-4">Send a message about this listing</p>
            <ContactForm listingId={id} authFetch={authFetch} />
          </div>

          {/* Agent Branding */}
          {listing.agent_branding && Object.keys(listing.agent_branding).length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Building2 size={14} className="text-slate-400" />
                <h3 className="font-bold text-sm text-slate-800">Listed By</h3>
              </div>
              <div className="flex items-center gap-3 mb-3">
                {listing.agent_branding.profile_photo ? (
                  <img src={listing.agent_branding.profile_photo} alt={listing.agent_branding.name}
                    className="w-12 h-12 rounded-full object-cover border border-slate-200 flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 text-lg font-black text-slate-400">
                    {listing.agent_branding.name?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-bold text-sm text-slate-800 truncate">{listing.agent_branding.name}</p>
                  {listing.agent_branding.title && (
                    <p className="text-xs text-slate-500 truncate">{listing.agent_branding.title}</p>
                  )}
                  {listing.agent_branding.logo_url && (
                    <img src={listing.agent_branding.logo_url} alt="Agency"
                      className="h-5 mt-1 object-contain" />
                  )}
                </div>
              </div>
              {listing.agent_branding.bio && (
                <p className="text-xs text-slate-600 mb-3 leading-relaxed bg-slate-50 rounded-lg p-2">
                  {listing.agent_branding.bio}
                </p>
              )}
              <div className="space-y-2">
                {listing.agent_branding.phone && (
                  <a href={`tel:${listing.agent_branding.phone}`}
                    className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 transition-colors">
                    <Phone size={13} className="text-slate-400 flex-shrink-0" />
                    {listing.agent_branding.phone}
                  </a>
                )}
                {listing.agent_branding.email && (
                  <a href={`mailto:${listing.agent_branding.email}`}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline transition-colors">
                    <Mail size={13} className="flex-shrink-0" />
                    {listing.agent_branding.email}
                  </a>
                )}
                {listing.agent_branding.website && (
                  <a href={listing.agent_branding.website} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline transition-colors">
                    <Globe size={13} className="flex-shrink-0" />
                    {listing.agent_branding.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
